import { vetor, type Vetor3 } from '../geometria/vetor3'
import {
  colunaDaRetencao,
  zDaColunaNaAltura,
  type AncoraOcupante,
  type ConfiguracaoCinto,
  type PerfilVeiculo,
} from './perfilVeiculo'

/**
 * Pontos do conjunto de retenção de uma posição de ocupante.
 *
 * Fica no domínio, e não no componente 3D, porque é geometria derivada do
 * perfil: assim dá para verificar por teste que nenhuma ancoragem cai fora da
 * carroceria — coisa que a perspectiva da cena esconde com facilidade.
 */
export interface PontosCinto {
  /** ancoragem superior na coluna; ausente no cinto subabdominal */
  ancoragemSuperior: Vetor3 | null
  /** ponto do ombro por onde a faixa diagonal passa */
  ombro: Vetor3 | null
  ancoragemInferiorExterna: Vetor3
  ancoragemInferiorInterna: Vetor3
  fecho: Vetor3
  quadrilExterno: Vetor3
  /** posição do pretensionador; ausente quando o perfil não o declara */
  pretensionador: Vetor3 | null
}

/** Altura da ancoragem superior acima da linha de cintura. */
const ALTURA_ANCORAGEM_SUPERIOR = 0.22
/** Afastamento da ancoragem em relação à chapa lateral. */
const RECUO_LATERAL = 0.12

export function pontosDoCinto(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  config: ConfiguracaoCinto,
): PontosCinto {
  const { x, y, z } = ancora.posicao
  const central = ancora.lado === 'central'
  const sinal = ancora.lado === 'direito' ? 1 : -1
  const tresPontos = config.tipo === 'tresPontos'

  // o ocupante central não tem parede lateral: as ancoragens nascem no banco
  const xExterno = central ? x - 0.22 : sinal * (perfil.largura / 2 - RECUO_LATERAL)
  const coluna = colunaDaRetencao(perfil, ancora.fila)
  const yAncoragem = perfil.cinturaY + ALTURA_ANCORAGEM_SUPERIOR

  const ancoragemSuperior = tresPontos
    ? vetor(xExterno, yAncoragem, zDaColunaNaAltura(perfil, coluna, yAncoragem))
    : null
  const ombro = tresPontos ? vetor(x + sinal * 0.12, y + 0.44, z + 0.08) : null

  return {
    ancoragemSuperior,
    ombro,
    ancoragemInferiorExterna: vetor(xExterno, ancora.alturaAssento - 0.04, z - 0.16),
    ancoragemInferiorInterna: vetor(
      central ? x + 0.22 : x - sinal * 0.24,
      ancora.alturaAssento - 0.02,
      z - 0.14,
    ),
    fecho: vetor(x - sinal * 0.2, ancora.alturaAssento + 0.04, z + 0.02),
    quadrilExterno: vetor(x + sinal * 0.19, ancora.alturaAssento + 0.05, z + 0.04),
    pretensionador: config.pretensionador
      ? vetor(xExterno, perfil.topoSoleiraY + 0.1, coluna.zBase)
      : null,
  }
}

/** Todos os pontos não nulos, para percorrer o conjunto de uma vez. */
export function todosOsPontos(pontos: PontosCinto): Vetor3[] {
  return [
    pontos.ancoragemSuperior,
    pontos.ombro,
    pontos.ancoragemInferiorExterna,
    pontos.ancoragemInferiorInterna,
    pontos.fecho,
    pontos.quadrilExterno,
    pontos.pretensionador,
  ].filter((p): p is Vetor3 => p !== null)
}

/** O ponto está dentro do envelope da carroceria? */
export function dentroDaCarroceria(perfil: PerfilVeiculo, p: Vetor3): boolean {
  return (
    Math.abs(p.x) <= perfil.largura / 2 &&
    p.y >= perfil.pisoY - 0.2 &&
    p.y <= perfil.tetoY &&
    p.z <= perfil.frenteZ &&
    p.z >= perfil.traseiraZ
  )
}

/** O ponto está dentro do habitáculo, entre a coluna A e a coluna C? */
export function dentroDoHabitaculo(perfil: PerfilVeiculo, p: Vetor3): boolean {
  return (
    dentroDaCarroceria(perfil, p) &&
    p.z <= perfil.colunaA.zBase &&
    p.z >= perfil.colunaC.zBase - 0.02
  )
}
